import React, { useContext, useEffect, useRef, useState } from 'react';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { EFileImportTypes } from 'enums/EFileExportTypes';
import {
  ConditionDTO,
  CustomLayerDTO,
  ExtendedIngredientDTO,
  GlobalIngredientsDTO,
  ImportIngredientsDTO,
  IngredientDTO,
  IngredientStockDTO,
  IngredientTypeDTO,
  LayerDTO,
} from 'business/models/Models';
import {
  GetIngredients,
  readFileAsBase64String,
  SetIngredients,
  showAlertSnackBar,
} from 'utils/Global';
import ExperimentService from 'business/services/ExperimentService';
import { initCustomLayer } from './PasteLayerHelper';
import { MIME_TYPES } from 'utils/MimeTypes';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useRMDispatch } from 'redux/hooks';
import {
  appendLayer,
  expandPlateRowInImport,
  setExperimentInfo,
} from 'redux/reducers/experimentDataReducer';
import { IngredientsImportManager } from 'components/main/Setup/Ingredients/conflictModal/IngredientsImportManager';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import RMModal from '../../modal/RMModal';
import RMButton from '../../customComponents/Controls/RMButton';
import { LoadingModal, useModalHandlers } from 'components/modal';
import { getPlainExperimentInfo } from 'transformers/experiment';

type FileImportViewProps = {
  docData: ExperimentDtoClient;
  importType: EFileImportTypes;
  setImportType: (e: EFileImportTypes) => void;
};

export const FileImportView = ({
  docData,
  importType,
  setImportType,
}: FileImportViewProps) => {
  const fileInputRef = useRef(null);
  const modelRef = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Processing');
  const [globalDataClone, setGlobalDataClone] = useState<GlobalIngredientsDTO>();
  const [isGlobalIngredientsUpdated, setIsGlobalIngredientsUpdated] = useState(false);
  const [conditions, setConditions] = useState<ConditionDTO[]>();
  const _expService = new ExperimentService();
  const dispatch = useRMDispatch();

  const [importedIngredients, setImportedIngredients] =
    useState<ImportIngredientsDTO>(null);
  const [tempImportedIngredients, setTempImportedIngredients] =
    useState<ImportIngredientsDTO>(null);
  const { importedIngredientCoflicts, setImportedIngredientCoflicts } =
    useContext(ExpDashboardContext);

  const [showConfirmationModal, setConfirmationShowModal] = useState<boolean>(false);
  const [shouldAddAdditionalStock, setShouldAddAdditionalStock] = useState<boolean>();
  const [importedfileData, setImportedfileData] = useState<string>();
  const { showModal, hideModal } = useModalHandlers();
  const [currentImportType, setCurrentImportType] = useState(importType);

  useEffect(() => {
    switch (importType) {
      case EFileImportTypes.XMLSCREEN:
        setCurrentImportType(importType);
        fileInputRef.current.accept = MIME_TYPES.xml;
        fileInputRef.current.click();
        setImportType(EFileImportTypes.NONE);
        break;
      case EFileImportTypes.TEXT:
        setCurrentImportType(importType);
        fileInputRef.current.accept = MIME_TYPES.txt;
        fileInputRef.current.click();
        setImportType(EFileImportTypes.NONE);
        break;
    }
  }, [importType]);

  useEffect(() => {
    if (importedIngredients) {
      setImportType(EFileImportTypes.NONE);
    }
  }, [importedIngredients]);

  useEffect(() => {
    const ingredients = GetIngredients();
    if (ingredients) {
      setGlobalDataClone(ingredients);
    }
  }, []);

  useEffect(() => {
    if (isGlobalIngredientsUpdated === true && conditions?.length) {
      const newLayer = getCustomLayer(conditions);
      addLayerOnCanvas(newLayer, conditions.length);
      setIsGlobalIngredientsUpdated(false);
    }
  }, [isGlobalIngredientsUpdated]);

  useEffect(() => {
    if (importedIngredientCoflicts) {
      let resolvedIngredientsList: ExtendedIngredientDTO[];
      resolvedIngredientsList = (importedIngredientCoflicts.conflictIngredientsList || [])
        .filter((ing) => ing && ing.resolvedIngredient)
        .map((ing) => ing.resolvedIngredient);
      const xmlIngredientsList = (
        importedIngredientCoflicts.nonConflictIngredientsList || []
      )
        .filter((ing) => ing && ing.xmlImportIngredient)
        .map((ing) => ing.xmlImportIngredient);
      resolvedIngredientsList = [...resolvedIngredientsList, ...xmlIngredientsList];
      let globalData: GlobalIngredientsDTO;
      if (!globalDataClone) {
        globalData = GetIngredients();
        setGlobalDataClone(globalData);
      } else {
        globalData = globalDataClone;
      }
      let ingredients: IngredientDTO[] = [...globalData.ingredients];
      let ingredientStocks: IngredientStockDTO[] = [...globalData.ingredientStock];
      let ingredientType: IngredientTypeDTO[] = [...globalData.ingredientType];
      const ingredientIngredientType = [...globalData.ingredientIngredientType];
      const ingredientsDtos = resolvedIngredientsList
        .filter((ingredient) => ingredient != null)
        .map((ingredient) => {
          let ing = null;
          if (ingredients.some((ingred) => ingred.id !== ingredient.id) === true) {
            ing = new IngredientDTO({
              id: ingredient.id,
              longName: ingredient.longName,
              shortName: ingredient.shortName,
              maxStockNumber: ingredient.maxStockNumber,
              defaultNonBufferIngredientStockId:
                ingredient.defaultNonBufferIngredientStockId,
              defaultBufferIngredientStockId: ingredient.defaultBufferIngredientStockId,
              bufferCalculationTypeId: ingredient.bufferCalculationTypeId,
              pKa: ingredient.pKa,
              exportIngredientAliasId: ingredient.exportIngredientAliasId,
              bufferDataPoint: ingredient.bufferDataPoint,
            });
          }

          if (ingredient.ingredientStock && Array.isArray(ingredient.ingredientStock)) {
            ingredientStocks = [...ingredientStocks, ...ingredient.ingredientStock];
          }

          if (ingredient.types && Array.isArray(ingredient.types)) {
            ingredientType = [
              ...ingredientType,
              ...ingredient.types.filter((type) => type && type.id < 0),
            ];
          }

          if (
            ingredient.ingredientIngredientType &&
            Array.isArray(ingredient.ingredientIngredientType)
          ) {
            ingredient.ingredientIngredientType.map((iit) => {
              if (
                iit &&
                ingredientIngredientType.some(
                  (ingIngType) =>
                    ingIngType.ingredientId == iit.ingredientId &&
                    ingIngType.id == iit.id &&
                    ingIngType.ingredientTypeId == iit.ingredientTypeId,
                ) === false
              ) {
                ingredientIngredientType.push(iit);
              }
            });
          }
          return ing;
        })
        .filter((ing) => ing !== null);

      if (ingredientsDtos.length > 0) {
        ingredientsDtos
          .filter((ingr) => ingr && ingr.id > 0)
          .map((ingredient) => {
            const tempIngIndex = ingredients.findIndex((ing) => ing.id === ingredient.id);
            if (tempIngIndex >= 0) {
              ingredients[tempIngIndex] = ingredient;
            }
          });
        ingredients = [...ingredients, ...ingredientsDtos.filter((ing) => ing.id < 0)];
      }
      const globalIngredients: GlobalIngredientsDTO = new GlobalIngredientsDTO();
      globalIngredients.ingredients = ingredients;
      globalIngredients.ingredientType = ingredientType;
      globalIngredients.ingredientStock = ingredientStocks;
      globalIngredients.ingredientIngredientType = ingredientIngredientType;
      globalIngredients.ingredientTypeColor = globalData.ingredientTypeColor;
      SetIngredients(globalIngredients);
      setIsGlobalIngredientsUpdated(true);
    }
  }, [importedIngredientCoflicts]);

  useEffect(() => {
    if (importedfileData && importedfileData !== '') {
      (async function () {
        try {
          if (shouldAddAdditionalStock === true) {
            setImportedIngredients(tempImportedIngredients);
          } else {
            const res = await _expService.parseXMlScreenFile(
              importedfileData,
              shouldAddAdditionalStock,
            );
            if (res) {
              setConditions(res.screenConditions);

              if (res.hasUnusedIngredientStocks === false) {
                setImportedfileData('');
                setImportedIngredients(res.importedIngredients);
              }
            }
          }
          setLoading(false);
        } catch (e) {
          console.error(e);
          setLoading(false);
        }
      })();
    }
  }, [shouldAddAdditionalStock]);

  async function fileImporthandler(e) {
    if (e.target.files.length === 0) {
      setImportType(EFileImportTypes.NONE);
      return;
    }
    const fileName = e.target.files[0].name;
    const idxDot = fileName.lastIndexOf('.') + 1;
    const extFile = fileName.substr(idxDot, fileName.length).toLowerCase();

    switch (currentImportType) {
      case EFileImportTypes.XMLSCREEN:
        if (extFile != 'xml') {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Please select a valid file.',
          );
          setImportType(EFileImportTypes.NONE);
          break;
        }
        setLoading(true);
        await readFileAsBase64String(e.target.files[0], processXmlScreenData);
        break;
      case EFileImportTypes.TEXT:
        if (extFile != 'txt') {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Please select a valid file.',
          );
          setImportType(EFileImportTypes.NONE);
          break;
        }
        setLoading(true);
        await readFileAsBase64String(e.target.files[0], processTextScreenData);
        break;
    }
    e.target.value = '';
  }

  async function processTextScreenData(base64String: string) {
    try {
      const textScreenConditions = await new ExperimentService().parseTextScreenFile(
        base64String,
      );
      const newLayer = getCustomLayer(textScreenConditions);
      await addLayerOnCanvas(newLayer, textScreenConditions.length);
    } catch (e) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, e.ErrorMessage);
    }
    setLoading(false);
    setImportType(EFileImportTypes.NONE);
  }

  async function processXmlScreenData(base64String: string) {
    if (base64String) {
      const res = await _expService.parseXMlScreenFile(base64String, null);
      if (!res) {
        setLoading(false);
      } else {
        setConditions(res.screenConditions);
        if (res.hasUnusedIngredientStocks === true) {
          setImportedfileData(base64String);
          setConfirmationShowModal(true);
          setTempImportedIngredients(res.importedIngredients);
        } else {
          setImportedIngredients(res.importedIngredients);
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
      showModal({
        body: 'Please select a valid file.',
        isOkOnly: true,
        onOk: () => {
          hideModal();
        },
        okButtonText: 'Ok',
      });
    }

    setImportType(EFileImportTypes.NONE);
  }

  const onClickModalConfirmation = (value: boolean) => {
    setShouldAddAdditionalStock(value);
    setConfirmationShowModal(false);
  };

  async function addLayerOnCanvas(newLayer: LayerDTO, numConditions: number) {
    let info;
    if (
      numConditions >
      docData.containerCols * docData.containerRows * docData.rowCount * docData.colCount
    ) {
      setLoadingText('Resizing Canvas');
      if (!docData.info) {
        info = getPlainExperimentInfo(
          await _expService.getExperimentInfo(docData.experimentId),
        );
      }
      const plateRows = Math.ceil(
        numConditions /
          (docData.containerCols * docData.containerRows * docData.colCount),
      );
      if (info) {
        dispatch(setExperimentInfo({ ...info }));
      }
      dispatch(expandPlateRowInImport(plateRows));
    }
    dispatch(appendLayer(newLayer));
  }

  function getCustomLayer(screenConditions: ConditionDTO[]) {
    const newLayer = initCustomLayer(docData, screenConditions.length);
    // Update Ids of Layer Conditions
    screenConditions.forEach((condition) => {
      condition.conditionListLayerId = (newLayer.layerDetail as CustomLayerDTO).id;
    });
    (newLayer.layerDetail as CustomLayerDTO).condition = screenConditions;
    return newLayer;
  }
  return (
    <>
      <IngredientsImportManager
        modelRef={modelRef}
        importedIngredients={importedIngredients}
        setResolvedIngredients={setImportedIngredientCoflicts}
        conditions={conditions}
        setConditions={setConditions}
        isScreen={true}
        hasAdditionalStock={shouldAddAdditionalStock}
      />
      <input
        id="fileimportview-input"
        className="hidden"
        ref={fileInputRef}
        type="file"
        onChange={fileImporthandler}
      />
      <LoadingModal showModal={isLoading} text={loadingText} />
      <RMModal
        id="fileimportview-confirmation-modal"
        showModal={showConfirmationModal}
        setShowModal={setConfirmationShowModal}
        body={
          <div id="fileimportview-confirmation-body" className="w-full">
            <label id="fileimportview-confirmation-label">
              The import file contains additional stocks from the vendor. Would you like
              to import the additional stocks?
            </label>
            <div
              id="fileimportview-confirmation-buttonwrapper"
              className="d-flex justify-content-center"
            >
              <div id="fileimportview-confirmation-action-yes-button" className="p-1">
                <RMButton
                  id="fileimportview-confirmation-button-yes"
                  onClick={() => onClickModalConfirmation(true)}
                  className="btn-outline-yellow-wide"
                  label="Yes"
                />
              </div>
              <div id="fileimportview-confirmation-action-no-button" className="p-1">
                <RMButton
                  id="fileimportview-confirmation-button-no"
                  onClick={() => onClickModalConfirmation(false)}
                  className="btn-outline-yellow-wide"
                  label="No"
                />
              </div>
            </div>
          </div>
        }
        heading="ROCK MAKER"
        showButtons={false}
      />
    </>
  );
};
